/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  autosaveCacheKeyPrefix,
  ClientType,
  ConfiguratorOrigin,
  ConfiguratorOriginsType,
  MeasurementsCoatProductType,
} from './constants';
import { imageDefinition } from './imageDefinition';
import { imageUrlBuilder } from './imageUrlProvider/imageUrlProvider';
import { IMAGE_TRANSFORMATION_DEFAULTS } from './imageUrlProvider/transformation';
import type {
  Fabric,
  FabricFilter,
  HiddenOptionsAndValues,
  NetworkError,
  Option,
  OptionsSelection,
  StylePreset,
  typeEnvironments,
  typeFabricList,
  typefilterAttributeName,
} from './interfaces';
import { getConfigurationTypeBySection, getSectionByConfigurationType, Section } from './navigation';
import type { ProductType, ProductTypeMeas } from './productDefinition';
import {
  additionalConfigurationTypeByProduct,
  ConfigurationTypes,
  MeasProductTypes,
  Products,
  rotationConfigurationTypes,
} from './productDefinition';

declare global {
  interface Window {
    analytics: any;
    DD_RUM: any;
    DD_LOGS: any;
  }
}

export const isFalsy = (value: number | boolean | undefined | null | string) => {
  // I know 'undefined' is not falsy but we need to catch it too
  const falsies = [false, 'false', 0, -0, '0', '', null, undefined, NaN, 'undefined'];

  return falsies.some(falsy => value === falsy);
};

export const isObjectEmpty = (objectToCheck: object) => {
  return objectToCheck && Object.keys(objectToCheck).length === 0 && objectToCheck.constructor === Object;
};

export const onEnter = (e: React.KeyboardEvent, cbFunc: (e: React.KeyboardEvent) => void) => {
  if (e.key === 'Enter') {
    cbFunc(e);
  }
};

export const getMEASProductTypeByConfigurationType = (configurationType: ConfigurationTypes | string) => {
  switch (configurationType) {
    case ConfigurationTypes.Jacket:
    case ConfigurationTypes.TuxedoJacket:
      return MeasProductTypes.Jacket;
    case ConfigurationTypes.Trousers:
    case ConfigurationTypes.TuxedoTrouser:
    case ConfigurationTypes.ExtraTrouser:
    case ConfigurationTypes.ExtraTuxedoTrouser:
      return MeasProductTypes.Trousers;
    case ConfigurationTypes.Waistcoat:
    case ConfigurationTypes.TuxedoWaistcoat:
      return MeasProductTypes.Waistcoat;
    case ConfigurationTypes.Shirt:
    case ConfigurationTypes.TuxedoShirt:
      return MeasProductTypes.Shirt;
    case ConfigurationTypes.Coat:
      return MeasProductTypes.OuterwearFormal;
    default:
      throw Error('No matching MEAS type for given CM configuration type');
  }
};

export const getConfigurationTypeIdByMeasurementsProductType = (product: ProductType, productType: ProductTypeMeas) => {
  switch (productType) {
    case 'Jacket':
      if (Products[product].includes(ConfigurationTypes.Jacket)) {
        return ConfigurationTypes.Jacket;
      } else if (Products[product].includes(ConfigurationTypes.TuxedoJacket)) {
        return ConfigurationTypes.TuxedoJacket;
      }
      break;
    case 'Trousers':
      if (Products[product].includes(ConfigurationTypes.Trousers)) {
        return ConfigurationTypes.Trousers;
      } else if (Products[product].includes(ConfigurationTypes.TuxedoTrouser)) {
        return ConfigurationTypes.TuxedoTrouser;
      }
      break;
    case 'Waistcoat':
      if (Products[product].includes(ConfigurationTypes.Waistcoat)) {
        return ConfigurationTypes.Waistcoat;
      } else if (Products[product].includes(ConfigurationTypes.TuxedoWaistcoat)) {
        return ConfigurationTypes.TuxedoWaistcoat;
      }
      break;
    case 'Shirt':
      if (Products[product].includes(ConfigurationTypes.Shirt)) {
        return ConfigurationTypes.Shirt;
      } else if (Products[product].includes(ConfigurationTypes.TuxedoShirt)) {
        return ConfigurationTypes.TuxedoShirt;
      }
      break;
    case MeasurementsCoatProductType:
      return ConfigurationTypes.Coat;

    default:
      throw Error('No matching configuration type ID for given product type');
  }
};

export const getModelFolderByProductType = (productType: ProductType): string => {
  switch (productType) {
    case 'Suit':
      return 'Jacket';
    case 'Tuxedo':
      return 'TuxedoJacket';
    case 'Jacket':
      return 'Blazer';
    case 'TuxedoJacket':
      return 'TuxedoBlazer';

    default:
      return productType;
  }
};

export const getCropPositionByProductType = (product: ProductType): string => {
  switch (product) {
    case 'Suit':
    case 'Tuxedo':
      return IMAGE_TRANSFORMATION_DEFAULTS.cropTop;
    default:
      return IMAGE_TRANSFORMATION_DEFAULTS.crop;
  }
};

export const calculateFabricPrice = (product: ProductType, prices: Record<number, number>) => {
  let price = 0;
  if (prices) {
    Products[product].forEach(configurationType => {
      if (!isAdditonalProduct(product, configurationType)) {
        const configurationTypePrice = prices[configurationType];
        if (configurationTypePrice) {
          price += configurationTypePrice;
        }
      }
    });
  }

  return price;
};

export const getImageUrl = (imageUrlTemplate: string | null, imageName: string | null, fabricImageName?: string) => {
  // Templates without the placeholder are unaffected by the substitution, so the no-fabric
  // case can fall back to the default image rather than emitting a literal `{fabricImageName}`.
  const fallbackUrl = `${imageUrlTemplate?.replace('{fabricImageName}', 'default')}/${imageName}`;

  if (!fabricImageName) {
    return { imageUrl: fallbackUrl, fallbackUrl };
  }

  return {
    imageUrl: `${imageUrlTemplate?.replace('{fabricImageName}', fabricImageName)}/${imageName}`,
    fallbackUrl,
  };
};

export const getThumbnails = (
  imagePath: {
    imageUrl: string;
    fallbackUrl: string;
  },
  cdnDomain: string,
  noTransformation?: boolean,
) => {
  const imageThumbnail = imageUrlBuilder({
    imageUrls: [imagePath.imageUrl],
    imageDefinition: imageDefinition.thumbnail,
    noTransformation,
    cdnDomain,
  }).src;

  const fallbackImageThumbnail = imageUrlBuilder({
    imageUrls: [imagePath.fallbackUrl],
    imageDefinition: imageDefinition.thumbnail,
    noTransformation,
    cdnDomain,
  }).src;

  return { imageThumbnail, fallbackImageThumbnail };
};

export const getImagePaths = (
  imageUrlTemplate: string | null,
  imageName: string,
  fabricImageName: string,
  cdnDomain: string,
  noTransformation?: boolean,
) => {
  return getThumbnails(getImageUrl(imageUrlTemplate, imageName, fabricImageName), cdnDomain, noTransformation);
};

export const checkConfigurationTypeRotationAvailable = (
  configurationType: ConfigurationTypes | undefined,
  isFetuareEnabled: boolean,
) => {
  return configurationType && isFetuareEnabled && rotationConfigurationTypes.includes(configurationType);
};

export const getConfigurationTypeNameByConfigurationType = (configurationType: ConfigurationTypes | string) => {
  switch (configurationType) {
    case ConfigurationTypes.Jacket:
      return 'CMTM Jacket';
    case ConfigurationTypes.Trousers:
    case ConfigurationTypes.ExtraTrouser:
      return 'CMTM Trousers';
    case ConfigurationTypes.Waistcoat:
      return 'CMTM Waistcoat';
    case ConfigurationTypes.Shirt:
      return 'CMTM Shirt';
    case ConfigurationTypes.Coat:
      return 'CMTM Coat';
    case ConfigurationTypes.TuxedoJacket:
      return 'CMTM Tuxedo Jacket';
    case ConfigurationTypes.TuxedoShirt:
      return 'CMTM Tuxedo Shirt';
    case ConfigurationTypes.TuxedoTrouser:
    case ConfigurationTypes.ExtraTuxedoTrouser:
      return 'CMTM Tuxedo Trouser';
    case ConfigurationTypes.TuxedoWaistcoat:
      return 'CMTM Tuxedo Waistcoat';
    default:
      return '';
  }
};

export const getProductIDByConfigurationType = (configurationType: ConfigurationTypes | string) => {
  const skuPrefix = 'CM';
  switch (configurationType) {
    case ConfigurationTypes.Jacket:
    case ConfigurationTypes.TuxedoJacket:
      return `${skuPrefix}-JACKET`;
    case ConfigurationTypes.Trousers:
    case ConfigurationTypes.TuxedoTrouser:
    case ConfigurationTypes.ExtraTrouser:
    case ConfigurationTypes.ExtraTuxedoTrouser:
      return `${skuPrefix}-TROUSERS`;
    case ConfigurationTypes.Waistcoat:
    case ConfigurationTypes.TuxedoWaistcoat:
      return `${skuPrefix}-WAISTCOAT`;
    case ConfigurationTypes.Shirt:
    case ConfigurationTypes.TuxedoShirt:
      return `${skuPrefix}-SHIRT`;
    case ConfigurationTypes.Coat:
      return `${skuPrefix}-COAT`;
    default:
      return '';
  }
};

export const capitalizeFirstLetter = (text: string) => {
  return text.charAt(0).toUpperCase() + text.slice(1);
};

export const buildAutosaveCacheKey = ({
  accountNr,
  product,
  client,
  origin,
  rtwProductCodes,
}: {
  accountNr?: string;
  product: ProductType;
  client: ClientType;
  origin: ConfiguratorOriginsType;
  rtwProductCodes: string[];
}) => {
  const productCodes = rtwProductCodes.length > 0 ? `:${rtwProductCodes.join(',')}` : '';
  return `${autosaveCacheKeyPrefix}${accountNr ? accountNr + ':' : ''}${product}:${client}:${origin}${productCodes}`;
};

export const isAdditonalProduct = (product: ProductType, configurationTypeId: ConfigurationTypes) => {
  if (!product || !configurationTypeId) {
    return false;
  }
  return (additionalConfigurationTypeByProduct[product] as ConfigurationTypes[]).indexOf(configurationTypeId) > -1;
};

export const isExtraConfigurationInProduct = (product: ProductType, configurationTypeId: ConfigurationTypes) => {
  if (!product || !configurationTypeId) {
    return false;
  }
  return isAdditonalProduct(product, configurationTypeId) && +configurationTypeId >= 100;
};

export const isExtraConfigurationSection = (section: Section) => {
  const configurationType = getConfigurationTypeBySection(section);
  return configurationType && +configurationType >= 100;
};

export const convertInputToArray = (input: string | string[]) => {
  if (!input) {
    return [];
  }

  if (Array.isArray(input)) {
    return input;
  }

  if (input.includes(',')) {
    return input.split(',');
  }

  return [input];
};

export const shouldAddAdditonalProduct = (isAdditionalProductAdded: Record<string, boolean>, product: ProductType, section: string | null) => {
  const affectedConfigurationTypeId = getConfigurationTypeBySection(section!);

  if (isExtraConfigurationInProduct(product!, affectedConfigurationTypeId)) {
    return false;
  }

  return (
    product &&
    isAdditonalProduct(product, affectedConfigurationTypeId) &&
    !isAdditionalProductAdded[affectedConfigurationTypeId]
  );
};

export const shouldShowRemoveAdditionalProduct = (isAdditionalProductAdded: Record<string, boolean>, product: ProductType, section: string | null) => {
  const affectedConfigurationTypeId = getConfigurationTypeBySection(section!);

  if (isExtraConfigurationInProduct(product!, affectedConfigurationTypeId)) {
    return false;
  }

  return (
    product &&
    isAdditonalProduct(product, affectedConfigurationTypeId) &&
    isAdditionalProductAdded[affectedConfigurationTypeId]
  );
};

export const getConfigurationTypeKey = (sectionConfigurationType: ConfigurationTypes): string => {
  switch (sectionConfigurationType) {
    case ConfigurationTypes.Jacket:
    case ConfigurationTypes.TuxedoJacket:
      return 'jacket';
    case ConfigurationTypes.Trousers:
    case ConfigurationTypes.TuxedoTrouser:
      return 'trousers';
    case ConfigurationTypes.Waistcoat:
    case ConfigurationTypes.TuxedoWaistcoat:
      return 'waistcoat';
    case ConfigurationTypes.Coat:
      return 'coat';
    case ConfigurationTypes.Shirt:
    case ConfigurationTypes.TuxedoShirt:
      return 'shirt';
    case ConfigurationTypes.ExtraTrouser:
    case ConfigurationTypes.ExtraTuxedoTrouser:
      return 'extraTrousers';
    default:
      return '';
  }
};

// productType translation keys are the ProductType value with a lowercase first letter,
// e.g. ProductType.TuxedoJacket -> 'productType.tuxedoJacket'.
export const getProductTypeKey = (product: ProductType): string => product.charAt(0).toLowerCase() + product.slice(1);

// Searchable option lists (Option.hasSearch) show a translated count of the items in the
// grid - "56 Buttons", "43 Colors". The API option `code` is the stable identifier for the
// kind of item being listed, so it drives the noun; unmapped codes get a generic label.
const ITEM_COUNT_KEY_BY_OPTION_CODE: Record<string, string> = {
  button: 'itemCount.buttons',
  lining: 'itemCount.colors',
};

export const getItemCountKey = (optionCode?: string): string =>
  ITEM_COUNT_KEY_BY_OPTION_CODE[optionCode ?? ''] ?? 'itemCount.items';

export const getProductText = (productType: ProductType | undefined) => {
  if (!productType) {
    return '';
  }

  if (productType === 'Tuxedo') {
    return 'tuxedo suit';
  }

  return productType.replace('Tuxedo', 'tuxedo ').toLowerCase();
};

// Example: filterValues=season:Fall/Winter_quality:Wool+Cashmere_color:Grey
export const processPreFilterFormat = (prefilters: string): Record<string, string[]> => {
  let selectedFilters = {};
  prefilters.split('_')?.map(filterStr => {
    const [filterName, filterValues] = filterStr.split(':');
    const values = filterValues.split(',');
    selectedFilters = { ...selectedFilters, [filterName + 'Name']: values };
  });
  return selectedFilters;
};

export const validatePreFilters = (
  selectedFilters: Record<string, string[]>,
  fabricFilters: FabricFilter[],
): Record<string, string[]> => {
  let validatedPreFilters = {};
  Object.keys(selectedFilters).forEach(filterName => {
    const filter = fabricFilters.find(({ filterAttribute }) => filterName === filterAttribute);
    if (!filter) {
      return;
    }
    const filterValues = selectedFilters[filterName].filter(filterValue => {
      return filter.items.some(filterItem => {
        if (filterItem.subItems) {
          return filterItem.subItems.some(({ displayName }) => filterValue === displayName);
        } else {
          return filterValue === filterItem.displayName;
        }
      });
    });
    if (filterValues.length) {
      validatedPreFilters = {
        ...validatedPreFilters,
        [filterName]: filterValues,
      };
    }
  });

  return validatedPreFilters;
};

export const isLowStockFabric = (fabric: Fabric) => {
  return fabric.stockStatus === 'LOW';
};

export const LOW_STOCK_PIECES_THRESHOLD = 15;

export const getLowStockPieces = ({ remainingPieces }: Fabric): number | null =>
  typeof remainingPieces === 'number' && remainingPieces <= LOW_STOCK_PIECES_THRESHOLD ? remainingPieces : null;

/**
 * Calculates which filter options are available based on current filter selections
 * Returns a map of filter attributes to available option display names
 */
export const getAvailableFilterOptions = (
  fabricList: typeFabricList,
  fabricSequence: number[],
  fabricFilters: FabricFilter[],
  selectedFilters: Record<string, string[]>,
): Record<string, Set<string>> => {
  const availableOptions: Record<string, Set<string>> = {};

  // Initialize available options for each filter
  fabricFilters.forEach(filter => {
    availableOptions[filter.filterAttribute] = new Set<string>();
  });

  // Get base fabric list (excluding low stock)
  const baseFabricIds = filterLowStockFabricList(fabricSequence.map(String), fabricList);
  
  // For each filter, calculate what options would be available if that filter was not applied
  fabricFilters.forEach(currentFilter => {
    // Create a temporary filter selection without the current filter
    const tempFilters = { ...selectedFilters };
    delete tempFilters[currentFilter.filterAttribute];
    
    // Get fabrics that match all other filters
    let availableFabricIds = [...baseFabricIds];
    
    // Apply all filters except the current one
    for (const filterName in tempFilters) {
      if (tempFilters[filterName].length > 0) {
        availableFabricIds = availableFabricIds.filter(
          (id: string) =>
            tempFilters[filterName].includes(fabricList[id][filterName as typefilterAttributeName]),
        );
      }
    }
    
    // Collect all possible values for the current filter from available fabrics
    availableFabricIds.forEach(fabricId => {
      const fabric = fabricList[fabricId];
      const filterValue = fabric[currentFilter.filterAttribute as typefilterAttributeName];
      if (filterValue) {
        availableOptions[currentFilter.filterAttribute].add(filterValue);
      }
    });
  });

  return availableOptions;
};

export const filterLowStockFabricList = (ids: string[], fabricList: typeFabricList) => {
  return ids.filter((id: string) => !isLowStockFabric(fabricList[id]));
};

export const getProductDefaultHiddenItemsState = (product: ProductType) => {
  const defaultHiddenState: HiddenOptionsAndValues = {
    optionsPerConfiguration: {},
    optionValuesPerConfiguration: {},
    monogramColors: [],
    monogramPositions: [],
  };

  Products[product].forEach(configurationType => {
    defaultHiddenState.optionsPerConfiguration[configurationType] = [];
    defaultHiddenState.optionValuesPerConfiguration[configurationType] = [];
  });

  return defaultHiddenState;
};

export const initialiseConfigurationBasedOptionsSelection = (product: ProductType) => {
  const configurationBasedOptionsSelection: OptionsSelection = {};

  Products[product].forEach(configurationType => {
    configurationBasedOptionsSelection[configurationType] = {};
  });

  return configurationBasedOptionsSelection;
};

export const getSingleOptions = (optionItem: Option[]) => {
  const items: Option[] = [];
  optionItem.forEach(option => {
    if (option.options) {
      option.options.forEach(subOption => {
        let name = subOption.name;
        if (option.name !== subOption.name) {
          name = `${option.name} ${subOption.name}`;
        }

        items.push({ ...subOption, name: name });
      });
    } else {
      items.push(option);
    }
  });
  return items;
};

// Replacing placeholder values in template indicated with {numeric_value}
// e.g. formatString('template example {0} with {1} values', one, two) returns => 'template example one with two values'
export const formatString = (template: string, ...values: (string | number)[]): string => {
  return template.replace(/{(\d+)}/g, (match, number) => {
    return typeof values[number] !== 'undefined' ? String(values[number]) : match;
  });
};

export const findFabricDefaultSelection = (
  fabricList: typeFabricList,
  fabricSequence: number[],
  defaultFabricId: string,
  fabricToPreselect?: Fabric,
) => {
  const filteredLowStockFabricList = filterLowStockFabricList(fabricSequence.map(String), fabricList);
  const defaultFabric = fabricList[defaultFabricId];
  let fabricToSelect = fabricToPreselect;
  if (!fabricToSelect) {
    fabricToSelect = defaultFabric ?? fabricList[filteredLowStockFabricList[0]];
  }

  return fabricToSelect;
};

export const getConfiguratorOrigin = (
  origin: ConfiguratorOrigin,
  preloadConfiguration: string,
  productCodes: string,
): ConfiguratorOrigin => {
  if (!isFalsy(preloadConfiguration)) {
    return ConfiguratorOrigin.Clone;
  }

  if (productCodes) {
    return ConfiguratorOrigin.CustomizeFlow;
  }

  return origin;
};

export const stepIncrement = 9;

export enum rotationPosition {
  front = 0,
  side = 1,
  back = 2,
}

export const rotationConfig = {
  front: {
    imageSuffix: 'R00',
    spriteStep: 0,
  },
  side: {
    imageSuffix: 'R01',
    spriteStep: 1 * stepIncrement,
  },
  back: {
    imageSuffix: 'R02',
    spriteStep: 2 * stepIncrement,
  },
};

export const isBrowserRender = () => typeof window !== 'undefined';

export const checkRotationStep = (option: Option | undefined) => {
  if (!option) {
    return rotationConfig.front;
  }

  switch (option.rotationPosition) {
    case rotationPosition.side:
      return rotationConfig.side;
    case rotationPosition.back:
      return rotationConfig.back;
    default:
      return rotationConfig.front;
  }
};

export const isProductBaseStyleActive = (
  selectedBaseStyle: Record<string, StylePreset>,
  section: string | null,
  configurationType?: string,
) => {
  let configurationTypeId = '';

  if (section && section !== Section.Fabric && section !== Section.Summary) {
    configurationTypeId = getConfigurationTypeBySection(section ?? '');
  }

  if (configurationType) {
    configurationTypeId = getConfigurationTypeBySection(getSectionByConfigurationType(configurationType ?? ''));
  }

  return !!selectedBaseStyle[configurationTypeId];
};

export const logConsole = (message: string, params: any, isLogEnabled: boolean) => {
  if (isLogEnabled && isBrowserRender()) {
    console.log(message, params);
  }
};

export const getDatadogErrorObject = (error: Error, apiUrl?: string, cmCorrelationId?: string) => {
  // custom error object is needed to be able separate errors in datadog original errors and centralize our errors
  let cause: Record<string, unknown> | unknown = error.cause;

  if (error.cause instanceof Error) {
    const causeAsNetworkError = error.cause as NetworkError;
    cause = {
      name: error.cause.name,
      message: error.cause.message,
      // undici error code (e.g. UND_ERR_CONNECT_TIMEOUT, UND_ERR_SOCKET) or Node.js errno code (e.g. ECONNREFUSED, ENOTFOUND)
      code: causeAsNetworkError.code,
      // syscall that failed (e.g. 'connect', 'read') — narrows where in the TCP handshake it broke
      syscall: causeAsNetworkError.syscall,
      // resolved IP and port — helps detect DNS misrouting or wrong upstream target
      address: causeAsNetworkError.address,
      port: causeAsNetworkError.port,
    };
  }

  return {
    customError: {
      errorName: error.name,
      errorStatusText: error.message,
      cause,
      stack: error.stack,
      apiUrl,
      cmCorrelationId,
    },
  };
};

// Deep comparison utility function
export const deepEqual = (obj1: unknown, obj2: unknown): boolean => {
  if (obj1 === obj2) return true;
  
  if (obj1 == null || obj2 == null) return obj1 === obj2;
  
  if (typeof obj1 !== typeof obj2) return false;
  
  if (typeof obj1 !== 'object') return obj1 === obj2;
  
  if (Array.isArray(obj1) && Array.isArray(obj2)) {
    if (obj1.length !== obj2.length) return false;
    for (let i = 0; i < obj1.length; i++) {
      if (!deepEqual(obj1[i], obj2[i])) return false;
    }
    return true;
  }
  
  if (Array.isArray(obj1) !== Array.isArray(obj2)) return false;
  
  const keys1 = Object.keys(obj1 as Record<string, unknown>);
  const keys2 = Object.keys(obj2 as Record<string, unknown>);
  
  if (keys1.length !== keys2.length) return false;
  
  for (const key of keys1) {
    if (!keys2.includes(key)) return false;
    if (!deepEqual((obj1 as Record<string, unknown>)[key], (obj2 as Record<string, unknown>)[key])) return false;
  }
  
  return true;
};

export const camelToSnakeCase = (str: string): string => {
  return str.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();
};

/**
 * Walks up from `element` to find a nested scroll container (e.g., a modal
 * or embedded panel). Returns null when the viewport itself is the scroll
 * container (body/html) so callers can fall back to window.
 */
export const getNestedScrollContainer = (element: HTMLElement | null): HTMLElement | null => {
  let parent = element?.parentElement ?? null;
  while (parent) {
    if (parent.classList.contains('custom-made')) {
      return null;
    }

    if (parent === document.body || parent === document.documentElement) {
      return document.body;
    }

    parent = parent.parentElement;
  }
  return document.body;
};

// Maps China-specific environments to their base equivalents so they can be
// passed to downstream SDKs/components that only understand the base envs.
export const normalizeEnvironment = (env: typeEnvironments | 'cn_tst' | 'cn_prd'): typeEnvironments => {
  switch (env) {
    case 'cn_tst':
      return 'tst';
    case 'cn_prd':
      return 'prd';
    default:
      return env;
  }
};